import { ChangeRequest, CarrierViewRecord } from '../../api/admin-api';
import { AppetiteView, useCarrierViewById, useCarrierViewByIds } from '../../hooks/useAdminApi';

function appetiteViewFor(table: string): AppetiteView | null {
    if (table === 'CarrierRecommendation') return 'classic';
    if (table === 'CarrierAppetiteByUkw') return 'universal';
    return null;
}

export const isBulkChangeRequest = (cr: ChangeRequest) =>
    Array.isArray(cr.targetRecordIds) && cr.targetRecordIds.length > 0;

function payloadEntries(changeData: Record<string, unknown>): [string, unknown][] {
    return Object.entries(changeData ?? {}).filter(([k]) => k !== 'TargetRecordIds' && k !== 'BulkSetup');
}

export interface BulkSetupSummary {
    view?: string;
    carrierId?: number;
    carrierName?: string;
    lineOfBusinessId?: number;
    lineOfBusiness?: string;
    states?: string[];
    rows?: {
        assocId?: number;
        usStateId?: number;
        keyword?: string;
        stateCode?: string;
        businessClass?: string;
        weight?: number;
    }[];
}

export function readBulkSetup(changeData: Record<string, unknown> | undefined): BulkSetupSummary | null {
    const raw = changeData?.['BulkSetup'];
    if (!raw) return null;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) as BulkSetupSummary;
        } catch {
            return null;
        }
    }
    if (typeof raw === 'object') return raw as BulkSetupSummary;
    return null;
}

const FIELD_LABELS: Record<string, string> = {
    Weight: 'Appetite Weight',
    CarrierId: 'Carrier ID',
    Carrier: 'Carrier',
    KeywordId: 'Keyword ID',
    Keyword: 'Keyword',
    LineOfBusiness: 'Line of Business',
    UsStateCode: 'State',
    BusinessClass: 'Business Class',
    Value: 'Carrier Business Class',
    Description: 'Carrier Business Class',
    IsCarrierActive: 'Carrier Active',
};

const fieldLabel = (key: string) => FIELD_LABELS[key] ?? key;

export function toNumber(v: unknown): number | undefined {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const jtokenMatch = v.match(/^\{"ValueKind"\s*:\s*(\d+)\}$/);
        if (jtokenMatch) {
            return parseInt(jtokenMatch[1], 10) === 6 ? 0 : 1;
        }
        const n = Number(v);
        return isNaN(n) ? undefined : n;
    }
    return undefined;
}

export function WeightBadge({ weight }: { weight: number | undefined }) {
    if (weight === undefined) return <span className="admin-muted">—</span>;
    const active = weight === 1;
    return <span className={`admin-badge ${active ? 'admin-badge-on' : 'admin-badge-off'}`}>{active ? 'Active' : 'Inactive'}</span>;
}

const Arrow = () => <span className="admin-arrow">→</span>;

function recordKeyword(rec: CarrierViewRecord) {
    return rec.keyword || [rec.primaryKeyword, rec.secondaryKeyword].filter(Boolean).join(' / ') || '—';
}

function renderFieldValue(key: string, val: unknown) {
    if (key === 'Weight') return <WeightBadge weight={toNumber(val)} />;
    return <span>{String(val ?? '—')}</span>;
}

function ChangeDiff({ cr, current }: { cr: ChangeRequest; current: CarrierViewRecord | undefined }) {
    const entries = payloadEntries(cr.changeData ?? {});
    const bulk = isBulkChangeRequest(cr);

    if (entries.length === 0)
        return <p className="admin-muted">No change data available.</p>;

    if (!bulk && current) {
        return (
            <div className="admin-diff">
                {entries.map(([k, newVal]) => {
                    const camelKey = k.charAt(0).toLowerCase() + k.slice(1) as keyof CarrierViewRecord;
                    const oldVal = current[camelKey] ?? (current as Record<string, unknown>)[k];
                    return (
                        <div key={k} className="admin-diff-row">
                            <span className="admin-diff-label">{fieldLabel(k)}</span>
                            {renderFieldValue(k, oldVal)}
                            <Arrow />
                            {renderFieldValue(k, newVal)}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="admin-diff">
            {entries.map(([k, v]) => (
                <div key={k} className="admin-diff-row admin-diff-row-new">
                    <span className="admin-diff-label">{fieldLabel(k)}</span>
                    {renderFieldValue(k, v)}
                </div>
            ))}
        </div>
    );
}

function BulkRecordsTable({
    ids, newWeight, records: provided, view,
}: {
    ids: number[];
    newWeight: number | undefined;
    records?: CarrierViewRecord[];
    view: AppetiteView;
}) {
    const fetched = useCarrierViewByIds(provided ? [] : ids, view);
    const records = provided ?? fetched.data;
    const isLoading = !provided && fetched.isLoading;
    const isError = !provided && (fetched.isError || !fetched.data);

    if (isLoading) {
        return <div className="admin-empty">Loading {ids.length} records…</div>;
    }

    if (isError || !records) {
        return <p className="admin-muted">Could not load record details.</p>;
    }

    const byId = new Map(records.map(rec => [rec.id, rec]));

    return (
        <div className="admin-table-scroll">
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Carrier</th>
                        <th>State</th>
                        <th>Line of Business</th>
                        <th>Keyword</th>
                        <th>Current</th>
                        <th></th>
                        <th>New</th>
                    </tr>
                </thead>
                <tbody>
                    {ids.map(id => {
                        const rec = byId.get(id);
                        if (!rec)
                            return (
                                <tr key={id}>
                                    <td colSpan={7} className="admin-muted">#{id} — could not load</td>
                                </tr>
                            );
                        return (
                            <tr key={rec.id}>
                                <td>{rec.carrierName}</td>
                                <td>{rec.stateCode}</td>
                                <td>{rec.lineOfBusiness}</td>
                                <td>{recordKeyword(rec)}</td>
                                <td><WeightBadge weight={toNumber(rec.weight)} /></td>
                                <td><Arrow /></td>
                                <td>
                                    {newWeight !== undefined
                                        ? <WeightBadge weight={newWeight} />
                                        : <span className="admin-muted">—</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export function bulkSetupCounts(setup: BulkSetupSummary) {
    const rows = setup.rows ?? [];
    const active = rows.filter(row => row.weight === 1).length;
    return { rows: rows.length, active, inactive: rows.length - active };
}

function BulkSetupPanel({ setup }: { setup: BulkSetupSummary }) {
    const rows = setup.rows ?? [];
    const counts = bulkSetupCounts(setup);
    const sample = rows.slice(0, 50);
    const carrier = setup.carrierName
        ? `${setup.carrierName} (${setup.carrierId ?? '—'})`
        : `Carrier ${setup.carrierId ?? '—'}`;
    const lob = setup.lineOfBusiness
        ? `${setup.lineOfBusiness} (${setup.lineOfBusinessId ?? '—'})`
        : `Line of business ${setup.lineOfBusinessId ?? '—'}`;

    return (
        <div className="admin-preview-section admin-preview-section-flush">
            <p className="admin-meta-label">
                {carrier} · {lob} · {setup.view ?? '—'} · {counts.rows} total · {counts.active} active, {counts.inactive} inactive
                {(setup.states ?? []).length > 0 ? ` · ${(setup.states ?? []).join(', ')}` : ''}
            </p>
            <div className="admin-table-scroll">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Assoc id</th>
                            <th>State id</th>
                            <th>Keyword</th>
                            <th>State</th>
                            <th>Business class</th>
                            <th>Appetite</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sample.map((row, index) => (
                            <tr key={`${row.assocId}-${row.usStateId}-${index}`}>
                                <td>{row.assocId ?? '—'}</td>
                                <td>{row.usStateId ?? '—'}</td>
                                <td>{row.keyword || '—'}</td>
                                <td>{row.stateCode || '—'}</td>
                                <td>{row.businessClass || '—'}</td>
                                <td><WeightBadge weight={row.weight} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {rows.length > sample.length && (
                <p className="admin-muted">Showing the first {sample.length} of {rows.length} rows.</p>
            )}
        </div>
    );
}

export function PreviewPanel({ cr, records }: { cr: ChangeRequest; records?: CarrierViewRecord[] }) {
    const bulk = isBulkChangeRequest(cr);
    const setup = readBulkSetup(cr.changeData);
    const appetiteView = appetiteViewFor(cr.targetTable);
    const isSingleUpdate = !bulk && !!cr.targetRecordId;
    const { data: current, isLoading } = useCarrierViewById(
        isSingleUpdate && appetiteView ? cr.targetRecordId : null,
        appetiteView ?? 'classic',
    );

    const bulkIds: number[] = bulk ? cr.targetRecordIds : [];
    const newWeight = bulk ? toNumber(cr.changeData?.['Weight']) : undefined;

    return (
        <div className="admin-preview">
            <p className="admin-preview-title">Preview</p>

            <div className="admin-preview-meta">
                <div>
                    <p className="admin-meta-label">Table</p>
                    <p className="admin-meta-value">{cr.targetTable}</p>
                </div>
                <div>
                    <p className="admin-meta-label">{setup ? 'Setup rows' : bulk ? 'Records' : 'Record ID'}</p>
                    <p className="admin-meta-value">
                        {setup
                            ? <span><strong>{setup.rows?.length ?? 0}</strong> rows</span>
                            : bulk
                            ? <span><strong>{bulkIds.length}</strong> records</span>
                            : (cr.targetRecordId ?? 'N/A')}
                    </p>
                </div>
                <div>
                    <p className="admin-meta-label">Operation</p>
                    <p className="admin-meta-value">Update</p>
                </div>
                {setup && (
                    <>
                        <div>
                            <p className="admin-meta-label">Carrier ID</p>
                            <p className="admin-meta-value">{setup.carrierId ?? 'N/A'}</p>
                        </div>
                        <div>
                            <p className="admin-meta-label">Line of Business ID</p>
                            <p className="admin-meta-value">{setup.lineOfBusinessId ?? 'N/A'}</p>
                        </div>
                    </>
                )}
            </div>

            {bulk && (
                <div className="admin-preview-section admin-preview-section-flush">
                    <p className="admin-meta-label">Affected Records — {bulkIds.length} total</p>
                    <BulkRecordsTable ids={bulkIds} newWeight={newWeight} records={records} view={appetiteView ?? 'classic'} />
                </div>
            )}

            {isSingleUpdate && appetiteView && (
                <div className="admin-preview-section">
                    <p className="admin-meta-label">Current record</p>
                    {isLoading ? (
                        <p className="admin-muted">Loading…</p>
                    ) : current ? (
                        <div className="admin-current-grid">
                            {([
                                ['Carrier', current.carrierName],
                                ['State', current.stateCode],
                                ['Line of Business', current.lineOfBusiness],
                                ['Keyword', recordKeyword(current)],
                            ] as [string, string][]).map(([label, val]) => (
                                <div key={label}>
                                    <p className="admin-meta-label">{label}</p>
                                    <p className="admin-meta-value">{val}</p>
                                </div>
                            ))}
                            <div>
                                <p className="admin-meta-label">Current Weight</p>
                                <WeightBadge weight={toNumber(current.weight)} />
                            </div>
                        </div>
                    ) : (
                        <p className="admin-muted">Could not load record details.</p>
                    )}
                </div>
            )}

            {setup && <BulkSetupPanel setup={setup} />}

            {!setup && (
            <div>
                <p className="admin-meta-label">What will change</p>
                <ChangeDiff cr={cr} current={current} />
            </div>
            )}

            {cr.comments && (
                <div className="admin-detail-block">
                    <p className="admin-meta-label">Reason</p>
                    <p className="admin-meta-value">{cr.comments}</p>
                </div>
            )}
        </div>
    );
}
